import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { BELOW_CHROME_DRAWER_PROPS, belowChromeDrawerPaperSx } from "../belowChromeDrawer";

function safeJsonParse(s) {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function prettyJson(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return "{}";
  }
}

const NODE_TYPES = [
  { value: "Start", label: "Start" },
  { value: "End", label: "End" },
  { value: "CreateLead", label: "CreateLead" },
  { value: "Tag", label: "Tag" },
  { value: "ChannelMessage", label: "ChannelMessage" },
  { value: "Condition", label: "Condition (if/else)" },
  { value: "Delay", label: "Delay" },
  // future: Chatbot, AIAgent, Product, Payment, Delivery, Survey, MultiChannelMessage
];

export default function NodeConfigPanel({ open, node, onClose, onChangeNode, onDeleteNode }) {
  const nodeId = node?.id ? String(node.id) : null;
  const initialLabel = String(node?.data?.label || "");
  const initialType = String(node?.data?.nodeType || "Action");
  const initialConfig = node?.data?.rawConfig && typeof node.data.rawConfig === "object" ? node.data.rawConfig : {};

  const [label, setLabel] = useState(initialLabel);
  const [type, setType] = useState(initialType);
  const [configDraft, setConfigDraft] = useState(prettyJson(initialConfig));

  useEffect(() => {
    setLabel(initialLabel);
    setType(initialType);
    setConfigDraft(prettyJson(initialConfig));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const parsedConfig = useMemo(() => safeJsonParse(configDraft), [configDraft]);
  const isConfigValid = parsedConfig != null && typeof parsedConfig === "object";

  const save = () => {
    if (!nodeId) return;
    const rawConfig = isConfigValid ? parsedConfig : initialConfig;
    onChangeNode?.(nodeId, {
      label,
      nodeType: type,
      rawConfig: rawConfig && typeof rawConfig === "object" ? rawConfig : {},
    });
  };

  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={onClose}
      ModalProps={BELOW_CHROME_DRAWER_PROPS.ModalProps}
      PaperProps={{
        sx: belowChromeDrawerPaperSx({ width: { xs: "100%", sm: 400 } }),
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography fontWeight={900}>Configurar node</Typography>
          <Typography variant="caption" color="text.secondary">
            {nodeId || "—"}
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {!nodeId ? (
          <Typography color="text.secondary">Selecione um node no canvas.</Typography>
        ) : (
          <Stack spacing={2}>
            <TextField label="Título" value={label} onChange={(e) => setLabel(e.target.value)} fullWidth />

            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select label="Tipo" value={type} onChange={(e) => setType(String(e.target.value))}>
                {NODE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Config (JSON)"
              value={configDraft}
              onChange={(e) => setConfigDraft(e.target.value)}
              fullWidth
              multiline
              minRows={10}
              spellCheck={false}
              error={!isConfigValid}
              helperText={
                !isConfigValid
                  ? "JSON inválido (salvaremos o último JSON válido)."
                  : "Use campos específicos por tipo (ex.: Tag.tag, ChannelMessage.text)."
              }
            />
          </Stack>
        )}
      </Box>

      {nodeId ? (
        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={save}>
              Aplicar
            </Button>
            <Button variant="outlined" color="error" onClick={() => onDeleteNode?.(nodeId)}>
              Remover node
            </Button>
            <Button variant="text" onClick={onClose}>
              Fechar
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Drawer>
  );
}

